import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  backLinkClasses,
  Badge,
  Button,
  Card,
  Filters,
  Icon,
  Page,
  PageHeader,
  SidePanel,
  Table,
  type Action,
} from '@evogard/ui';
import { formatInstant } from '@evogard/domain';
import type {
  Classification,
  ClassificationState,
  StatusDomain,
} from '@evogard/contract';
import { useTablePage } from '../../lib/list-search.js';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { useClassificationState } from './common.js';

/**
 * O que a **casa** decide sobre cada código de situação do ERP, por praça.
 *
 * ## Três perguntas, e por que `pago` não é uma delas
 *
 * Cancelado, excluído e bloqueia cancelamento. "Este boleto está pago" não
 * entra: quem responde isso é o próprio ERP, que devolve o campo, e o espelho
 * guarda o que ele diz. Uma quarta caixa aqui seria a quinta definição de
 * "boleto pago" desta empresa — e as outras quatro discordavam entre si.
 *
 * ## Por que a tabela nasce vazia, e por que isso não é um erro
 *
 * O mesmo código significa coisas diferentes em cada praça — o registro diz que
 * o 15 é "cancelamento diretoria" numa base e "sindicância" na outra, **sem
 * dizer qual é qual**. Semear por semelhança seria inventar exatamente a metade
 * que decide se um veículo aceita cancelamento. Regra 14: o que não dá para
 * entender fica de fora, inteiro, e a tela diz que não faz aquilo.
 *
 * ## Arquivar, nunca apagar
 *
 * Convenção 9, e mais do que isso: o código arquivado volta a ser **não
 * classificado**, que é um estado visível. Zerar as três caixas no lugar de
 * arquivar faria o veículo bloqueado voltar a aceitar cancelamento sem nada na
 * tela dizendo isso.
 */
export function StatusClassificationScreen() {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const canEdit = can(context, 'erp.mirror', 'editar');
  const [showArchived, setShowArchived] = useState(false);
  /* a página de cada tabela, na URL — o "voltar" devolve a página */
  const pages = useSearch({ from: '/authenticated/erp/classificacoes' });
  const pendingPage = useTablePage('pendentes', pages.pendentes);
  const classifiedPage = useTablePage('classificadas', pages.classificadas);
  /* O painel de ações fecha antes de a ação rodar, então a recusa de arquivar
   * aparece aqui em cima e não na linha. */
  const [actionError, setActionError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const query = useClassificationState(showArchived);

  const archive = useMutation({
    mutationFn: async (c: Classification) => {
      const r = await api.erp.archiveClassification({ params: { id: c.id } });
      return ok(r, 200, 'Não foi possível arquivar.');
    },
    onMutate: () => setActionError(null),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['erp'] }),
    onError: (e: Error) => setActionError(e.message),
  });

  const estado = query.data;
  const mirrorNever = estado?.mirror.every((e) => e.estado === 'nunca') ?? false;
  const decidedPending = estado?.decided.pending ?? [];

  return (
    <Page measure="cheia">
      <PageHeader
        title="Classificação de situações"
        back={
          <Link
            to="/erp/saude"
            activeOptions={{ exact: true }}
            className={backLinkClasses()}
          >
            <Icon nome="seta-esquerda" size={16} />
            Saúde da integração
          </Link>
        }
      >
        Vale para a base{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        O mesmo código pode significar coisas diferentes na outra praça — por isso cada
        uma classifica a sua.
      </PageHeader>

      {query.isError && (
        <Alert tone="erro" title="Não foi possível carregar a classificação.">
          {query.error.message}
        </Alert>
      )}

      {actionError && <Alert tone="erro">{actionError}</Alert>}

      {/* Só afirma "nada classificado" depois de ler. Antes disso, não há
          afirmação nenhuma a fazer. */}
      {estado && estado.classifications.length === 0 && (
        <Alert tone="alerta" title="Nenhuma situação classificada nesta base.">
          <p className="mt-1">
            É o estado esperado hoje, e não uma falha de carregamento. Enquanto estiver
            assim, quem pergunta a situação de um boleto ou de um veículo recebe{' '}
            <strong className="font-medium">não classificado</strong>: nenhum módulo
            decide cancelamento por código nenhum, e nenhuma esteira supõe no lugar.
          </p>
          <p className="mt-2">
            {mirrorNever
              ? 'O espelho de situações nunca sincronizou nesta base, então a plataforma ainda não sabe nem quais códigos existem. O primeiro passo é medir o ERP nas duas praças (make sga-medir); a lista abaixo se preenche sozinha quando o espelho passar.'
              : 'Os códigos que o espelho já trouxe estão listados abaixo, prontos para classificar.'}
          </p>
        </Alert>
      )}

      {!canEdit && (
        <Alert tone="informacao">
          Você vê a classificação desta base e não pode alterá-la. Quem administra a
          plataforma concede a ação <strong className="font-medium">editar</strong> em{' '}
          <strong className="font-medium">erp.espelho</strong>, em Grupos.
        </Alert>
      )}

      {(!query.isError || estado) && (
        <>
          <Card
            title="Códigos ainda não classificados"
            descricao="Apareceram no espelho e a casa ainda não disse o que eles significam."
            bleed
            actions={
              canEdit && decidedPending.length > 0 ? (
                <Button size="sm" onClick={() => setApplying(true)}>
                  Aplicar a classificação decidida ({decidedPending.length})
                </Button>
              ) : undefined
            }
          >
            <Table
              {...pendingPage}
              label="Códigos de situação ainda não classificados"
              items={estado?.notClassified ?? []}
              loading={query.isPending}
              rowKey={(s) => `${s.domain}:${s.code}`}
              empty={
                mirrorNever
                  ? 'O espelho de situações nunca sincronizou nesta base — a plataforma ainda não sabe quais códigos existem. Esta lista não está vazia: ela está por saber.'
                  : 'Todo código que o espelho trouxe já está classificado.'
              }
              rowTitle={(s) => `${domainLabel(s.domain)} ${s.code}`}
              rowSubtitle={(s) => s.descricao ?? 'Sem descrição no espelho'}
              rowIcon="erp"
              /*
               * Classificar é uma AÇÃO sobre a linha, e desde 17/09 ela mora
               * onde as ações moram: no painel das reticências.
               *
               * Ela era uma coluna chamada "Classificar" com as três caixas e
               * dois botões desenhados DENTRO da célula — a mesma decisão que a
               * tabela de baixo já tomava por `form`, tomada de outro jeito na
               * tabela de cima, uma embaixo da outra na mesma tela. O
               * formulário é o mesmo objeto nos dois lugares; o que muda agora
               * é só que ele não desenha mais uma coluna de larguras próprias
               * dentro de uma grade.
               */
              rowActions={(s): Action[] =>
                canEdit
                  ? [
                      {
                        label: 'Classificar',
                        icon: 'etiqueta',
                        form: (close) => (
                          <Form domain={s.domain} code={s.code} toClose={close} />
                        ),
                      },
                    ]
                  : []
              }
              columns={[
                { title: 'Domínio', cell: (s) => domainLabel(s.domain) },
                {
                  title: 'Código',
                  cell: (s) => <span className="font-mono text-xs">{s.code}</span>,
                },
                {
                  title: 'Descrição no ERP',
                  minWidth: '14rem',
                  cell: (s) =>
                    s.descricao ?? (
                      // a descrição é do fornecedor e nunca é copiada para a
                      // nossa tabela; quando ele não manda, a tela diz isso
                      <span className="text-texto-suave">sem descrição no espelho</span>
                    ),
                },
              ]}
            />
          </Card>

          <Card
            title="Classificadas"
            descricao="O que esta base decidiu sobre cada código."
            bleed
          >
            <Table
              {...classifiedPage}
              /*
               * "Mostrar arquivadas" era uma caixa de marcar solta no cabeçalho
               * do cartão, e virou o painel de filtros — que é onde o recorte
               * de toda tabela da casa mora desde 17/09.
               *
               * De caixa para `selecao` de duas opções pelo motivo escrito em
               * `FilterField`: dentro de um painel, a caixa de marcar não diz
               * qual é o outro estado dela. "Só as vigentes" e "Vigentes e
               * arquivadas" dizem os dois.
               */
              toolbar={
                <Filters
                  values={{ archived: showArchived ? 'sim' : '' }}
                  onApply={(values) => setShowArchived(values.archived === 'sim')}
                  fields={[
                    {
                      type: 'selecao',
                      name: 'archived',
                      label: 'Arquivadas',
                      placeholder: 'Só as vigentes',
                      options: [{ value: 'sim', label: 'Vigentes e arquivadas' }],
                    },
                  ]}
                />
              }
              label="Códigos de situação já classificados"
              items={estado?.classifications ?? []}
              loading={query.isPending}
              rowKey={(c) => String(c.id)}
              empty={
                'Nenhuma situação classificada nesta base. ' +
                'A tabela nasce vazia de propósito: o significado de cada código vem da medição do ERP, não de suposição.'
              }
              rowTitle={(c) => `${domainLabel(c.domain)} ${c.code}`}
              rowSubtitle={(c) => `Atualizada em ${formatInstant(c.updatedAt)}`}
              rowIcon="erp"
              rowActions={(c): Action[] => {
                if (!canEdit) return [];
                return [
                  {
                    label: 'Reclassificar',
                    icon: 'etiqueta',
                    /* `form`: são três perguntas de sim/não e uma justificativa,
                       e o painel troca de conteúdo no lugar. */
                    form: (close) => (
                      <Form domain={c.domain} code={c.code} initial={c} toClose={close} />
                    ),
                  },
                  {
                    label: 'Arquivar',
                    icon: 'bloqueado',
                    /* O motivo escrito, e não o botão sumindo: antes disto o
                       botão simplesmente não era renderizado para a já
                       arquivada, e nada dizia por que uma linha tinha duas
                       ações e a vizinha, uma. */
                    disabled: c.archivedAt
                      ? 'Esta classificação já está arquivada.'
                      : (false as const),
                    run: () => archive.mutate(c),
                  },
                ];
              }}
              columns={[
                { title: 'Domínio', cell: (c) => domainLabel(c.domain) },
                {
                  title: 'Código',
                  cell: (c) => (
                    <span className="font-mono text-xs">
                      {c.code}
                      {c.archivedAt && (
                        <Badge tone="neutro" className="ml-2">
                          Arquivada
                        </Badge>
                      )}
                    </span>
                  ),
                },
                { title: 'Cancelado', cell: (c) => <Brand enabled={c.canceled} /> },
                { title: 'Excluído', cell: (c) => <Brand enabled={c.deleted} /> },
                {
                  title: 'Bloqueia cancelamento',
                  cell: (c) => <Brand enabled={c.blocksCancellation} />,
                },
                { title: 'Troca', cell: (c) => <Brand enabled={c.vehicleSwap} /> },
                {
                  title: 'Entrada de troca',
                  cell: (c) => <Brand enabled={c.swapEntry} />,
                },
                {
                  title: 'Atualizada',
                  cell: (c) => (
                    <span className="whitespace-nowrap text-xs text-texto-suave">
                      {formatInstant(c.updatedAt)}
                    </span>
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}

      {estado && (
        <ApplyDecided
          open={applying}
          onOpenChange={setApplying}
          decided={estado.decided}
        />
      )}

      <Card title="O que cada resposta decide">
        <dl className="flex flex-col gap-2 text-sm">
          <div>
            <dt className="font-medium">Cancelado</dt>
            <dd className="text-texto-suave">
              O código quer dizer que o registro foi cancelado. É o que a regra de adoção
              usa para não adotar um boleto cancelado depois de uma escrita ambígua.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Excluído</dt>
            <dd className="text-texto-suave">
              Registro descartado na origem. É a primeira coisa que a regra de adoção joga
              fora — antes de qualquer outra pergunta.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Bloqueia cancelamento</dt>
            <dd className="text-texto-suave">
              Com este código, o veículo não aceita pedido de cancelamento. É a lista que
              hoje mora na cabeça de quem opera, e diverge entre CE e GO.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Troca</dt>
            <dd className="text-texto-suave">
              Só em veículo. A recorrência conta a partir do 2º boleto pago do veículo — o
              1º é a entrada do cliente. Na troca (de veículo, de titularidade, a
              inadimplente ou a inativa) o carro novo continua o contrato de quem já
              pagava, e conta desde o 1º boleto.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Entrada de troca</dt>
            <dd className="text-texto-suave">
              Só em veículo. O carro que entrou numa troca não é venda nova, e a premiação
              direta não o conta — mesmo quando a adesão foi cadastrada como nova cotação.
              O carro que saiu da troca foi venda nova no mês dele, e conta.
            </dd>
          </div>
          <div>
            <dt className="font-medium">E “pago” não está aqui</dt>
            <dd className="text-texto-suave">
              Porque quem responde é o ERP, que devolve o campo. O espelho guarda o que
              ele diz; a casa decide só o que ele não responde.
            </dd>
          </div>
        </dl>
      </Card>
    </Page>
  );
}

function domainLabel(domain: StatusDomain): string {
  return domain === 'boleto' ? 'Boleto' : 'Veículo';
}

/**
 * Sim ou não, com palavra — nunca só um ponto colorido.
 *
 * Estado é cor **e** símbolo **e** texto. Uma coluna de bolinhas verdes e
 * cinzas obrigaria quem não distingue as duas a adivinhar qual código bloqueia
 * cancelamento.
 */
function Brand({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <Badge tone="informacao">Sim</Badge>
  ) : (
    <span className="text-texto-suave">Não</span>
  );
}

/* ------------------------------------------------------------------------- *
 * Aplicar a classificação decidida
 * ------------------------------------------------------------------------- */

/**
 * A decisão da casa que está no arquivo da praça, gravada de uma vez.
 *
 * Nasceu em 28/10/2026: a homologação ficou com as situações de veículo da CE
 * na fila — decididas desde 21/09 em `erp-classification-ce.csv` —, porque o
 * arquivo só chegava ao banco pelo seed de desenvolvimento. Digitar dezenas de
 * códigos com cinco caixas cada um é onde nasce o erro.
 *
 * **A lista aparece antes de gravar**, com as cinco respostas de cada código:
 * quem clica vê o que vai entrar. E o que já está classificado não aparece nem
 * é tocado — quem classificou pela tela decidiu por cima do arquivo.
 */
function ApplyDecided({
  open,
  onOpenChange,
  decided,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  decided: ClassificationState['decided'];
}) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const apply = useMutation({
    mutationFn: async () => {
      const r = await api.erp.applyDecidedClassifications({ body: {} });
      return ok(r, 200, 'Não foi possível aplicar a classificação.');
    },
    onMutate: () => setError(null),
    onSuccess: () => {
      onOpenChange(false);
      void client.invalidateQueries({ queryKey: ['erp'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="larga"
      title="Aplicar a classificação decidida"
      descricao={`${decided.pending.length} código(s) que a casa já decidiu e esta base ainda não tem. O que já está classificado aqui fica como está.`}
      actions={
        <>
          <Button
            loading={apply.isPending}
            disabled={decided.pending.length === 0}
            onClick={() => apply.mutate()}
          >
            Aplicar {decided.pending.length}
          </Button>
          <Button variant="contorno" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
        </>
      }
    >
      {error && <Alert tone="erro">{error}</Alert>}
      <Table
        label="Classificação que será aplicada"
        items={decided.pending}
        rowKey={(d) => `${d.domain}:${d.code}`}
        empty="Nada a aplicar: tudo o que a casa decidiu já está classificado nesta base."
        rowTitle={(d) => `${domainLabel(d.domain)} ${d.code}`}
        rowSubtitle={(d) => d.descricao ?? 'Sem descrição no espelho'}
        rowIcon="erp"
        columns={[
          { title: 'Domínio', cell: (d) => domainLabel(d.domain) },
          {
            title: 'Código',
            cell: (d) => <span className="font-mono text-xs">{d.code}</span>,
          },
          {
            title: 'Descrição no ERP',
            minWidth: '12rem',
            cell: (d) =>
              d.descricao ?? (
                <span className="text-texto-suave">sem descrição no espelho</span>
              ),
          },
          { title: 'Cancelado', cell: (d) => <Brand enabled={d.canceled} /> },
          { title: 'Excluído', cell: (d) => <Brand enabled={d.deleted} /> },
          {
            title: 'Bloqueia cancelamento',
            cell: (d) => <Brand enabled={d.blocksCancellation} />,
          },
          { title: 'Troca', cell: (d) => <Brand enabled={d.vehicleSwap} /> },
          { title: 'Entrada de troca', cell: (d) => <Brand enabled={d.swapEntry} /> },
        ]}
      />
    </SidePanel>
  );
}

/* ------------------------------------------------------------------------- *
 * Classificar e reclassificar
 * ------------------------------------------------------------------------- */

/**
 * As cinco respostas, e o `PUT` que grava uma só.
 *
 * O mesmo formulário serve para classificar um código novo e para reclassificar
 * um que já existe: a rota cria ou atualiza pelo par `(domínio, código)`, e ter
 * dois formulários seria ter duas cópias das três perguntas — a segunda ficando
 * para trás no dia em que uma quarta aparecer.
 *
 * **Nenhuma caixa tem valor padrão escondido.** As três nascem desmarcadas na
 * criação porque a pessoa está declarando, não confirmando — e o contrato
 * recusa campo omitido em vez de assumir `false`. Um `false` silencioso aqui é
 * como um código que bloqueia cancelamento passa a não bloquear sem ninguém
 * decidir isso.
 */
function Form({
  domain,
  code,
  initial,
  toClose,
}: {
  domain: StatusDomain;
  code: string;
  initial?: Pick<
    Classification,
    'canceled' | 'deleted' | 'blocksCancellation' | 'vehicleSwap' | 'swapEntry'
  >;
  toClose?: () => void;
}) {
  const client = useQueryClient();
  const [canceled, setCanceled] = useState(initial?.canceled ?? false);
  const [deleted, setDeleted] = useState(initial?.deleted ?? false);
  const [blocks, setBlocks] = useState(initial?.blocksCancellation ?? false);
  const [swap, setSwap] = useState(initial?.vehicleSwap ?? false);
  const [entry, setEntry] = useState(initial?.swapEntry ?? false);
  const [error, setError] = useState<string | null>(null);

  const write = useMutation({
    mutationFn: async () => {
      const r = await api.erp.defineClassification({
        body: {
          domain,
          code,
          canceled,
          deleted,
          blocksCancellation: blocks,
          /* só veículo tem troca; em boleto a resposta é sempre "não" */
          vehicleSwap: domain === 'veiculo' && swap,
          swapEntry: domain === 'veiculo' && entry,
        },
      });
      return ok(r, 200, 'Não foi possível gravar a classificação.');
    },
    onSuccess: () => {
      setError(null);
      toClose?.();
      /*
       * `invalidateQueries` e não `setQueryData` com o corpo da resposta.
       *
       * A resposta traz a tela inteira, mas do filtro **padrão** — e esta
       * página pode estar mostrando as arquivadas. Guardá-la na chave aberta
       * faria as arquivadas sumirem da tela sem ninguém ter desmarcado a caixa.
       * A saúde também é relida: o número de códigos pendentes muda com isto.
       */
      void client.invalidateQueries({ queryKey: ['erp'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    /*
     * Alinhado à esquerda, e empilhado. Até 17/09 ele era `items-end` com as
     * três caixas em fileira, porque desenhava dentro de uma célula da tabela
     * de códigos não classificados. Ele só vive em painel agora, e painel é
     * formulário: as caixas descem uma por linha, que é onde uma pergunta de
     * sim/não com rótulo longo — "Bloqueia cancelamento" — se lê.
     */
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        write.mutate();
      }}
    >
      {error && <Alert tone="erro">{error}</Alert>}

      <div className="flex flex-col">
        <Checkbox label="Cancelado" marked={canceled} onChange={setCanceled} />
        <Checkbox label="Excluído" marked={deleted} onChange={setDeleted} />
        <Checkbox label="Bloqueia cancelamento" marked={blocks} onChange={setBlocks} />
        {domain === 'veiculo' && (
          <Checkbox
            label="Troca — a recorrência conta desde o 1º boleto"
            marked={swap}
            onChange={setSwap}
          />
        )}
        {domain === 'veiculo' && (
          <Checkbox
            label="Entrada de troca — a direta não conta o veículo"
            marked={entry}
            onChange={setEntry}
          />
        )}
      </div>

      <div className="flex gap-2">
        <Button type="submit" loading={write.isPending}>
          {initial ? 'Salvar' : 'Classificar'}
        </Button>
        {toClose && (
          <Button type="button" variant="contorno" onClick={toClose}>
            Cancelar
          </Button>
        )}
      </div>
    </form>
  );
}

function Checkbox({
  label,
  marked,
  onChange,
}: {
  label: string;
  marked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex min-h-11 items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={marked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 rounded border-borda-forte accent-primaria-texto"
      />
      {label}
    </label>
  );
}

/**
 * Reclassificar e arquivar.
 *
 * Arquivar não é uma ação de permissão nova — arquivar é editar. E o efeito
 * está escrito no botão: o código volta a ser **não classificado**, e não
 * "passa a não bloquear". São coisas diferentes, e confundi-las é o defeito que
 * aparece semanas depois como "o veículo bloqueado voltou a aceitar
 * cancelamento".
 */
